import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Loader2, Send, Sparkles, Heart, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import StarRating from "../components/StarRating";
import WizardTimeline from "../components/WizardTimeline";
import { STEPS, TOTAL_STEPS } from "../data/questions";
import { appreciate, appreciateStars } from "../data/appreciation";
import { submitFeedback } from "../lib/api";

const initialBasic = {
  parent_name: "",
  student_name: "",
  university_name: "SEU – Georgian National University, Georgia",
  batch_year: "",
  contact_number: "",
  city_branch: "",
};

export default function Feedback() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [basic, setBasic] = useState(initialBasic);
  const [answers, setAnswers] = useState({}); // q1..q40
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const current = STEPS[step];
  const progress = useMemo(() => Math.round(((step + (done ? 1 : 0)) / TOTAL_STEPS) * 100), [step, done]);

  const validateStep = () => {
    if (step === 0) {
      for (const f of current.fields) {
        if (f.required && !basic[f.key]?.trim()) {
          toast.error(`Please fill in: ${f.label}`);
          return false;
        }
      }
      return true;
    }
    // Other steps: at least require radio + star answered; textareas/checkbox optional
    for (const q of current.questions) {
      if (q.type === "radio" && !answers[q.id]) {
        toast.error(`Please answer question ${q.n}`);
        return false;
      }
      if (q.type === "stars" && !answers[q.id]) {
        toast.error(`Please give a star rating`);
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    if (step < TOTAL_STEPS - 1) {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const prev = () => {
    if (step > 0) setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      await submitFeedback({ ...basic, answers });
      setDone(true);
      toast.success("Thank you! Your feedback has been recorded.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-sand-50">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="thank-you-screen">
          <div className="w-16 h-16 rounded-full bg-saffron-100 text-saffron-600 mx-auto flex items-center justify-center">
            <Sparkles className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-6 text-stone-900">Thank you, truly.</h1>
          <p className="text-stone-600 mt-5 text-lg leading-relaxed">
            Your feedback helps us guide the next family with even more care. The Atmia Education team
            reads every response and uses it to improve counselling, accommodation, food at
            <span className="italic"> Atmia Saffron Kingdom</span> and life at SEU.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3">
            <Link to="/" data-testid="thank-you-home-btn" className="inline-flex items-center bg-stone-900 hover:bg-stone-800 text-white rounded-full px-6 py-3 font-medium">
              Back to Home
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <Header minimal />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-14">
        {/* Hero header */}
        <div className="mb-6 sm:mb-8">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-saffron-600 font-medium">Feedback Form · ~5 min</div>
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-2 sm:mt-3 text-stone-900 leading-tight">
            Help Atmia Education serve you better.
          </h1>
          <p className="text-stone-600 mt-2 sm:mt-3 max-w-2xl text-sm sm:text-base">
            Your answers are confidential and shared only with the Atmia team. There are no right or wrong answers.
          </p>
        </div>

        {/* Vibrant digital timeline */}
        <div className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 grain-bg overflow-hidden relative">
          <WizardTimeline
            current={step}
            total={TOTAL_STEPS}
            percent={progress}
            onJump={(i) => i <= step && setStep(i)}
          />
        </div>

        {/* Form */}
        <div className="md:mt-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="bg-white border border-stone-200 rounded-2xl p-5 sm:p-7 md:p-10"
              data-testid={`step-card-${current.id}`}
            >
              <div className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-saffron-600 font-medium">{current.subtitle}</div>
              <h2 className="font-serif text-xl sm:text-2xl md:text-3xl mt-1.5 sm:mt-2 text-stone-900">{current.title}</h2>

              <div className="mt-6 sm:mt-8 space-y-7 sm:space-y-8">
                {step === 0 ? (
                  <BasicFields fields={current.fields} basic={basic} setBasic={setBasic} />
                ) : (
                  current.questions.map((q) => (
                    <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
                  ))
                )}
              </div>

              <div className="mt-8 sm:mt-10 pt-5 sm:pt-6 border-t border-stone-100 flex items-center justify-between gap-3 sm:gap-4">
                <button
                  type="button" onClick={prev} disabled={step === 0}
                  data-testid="wizard-prev-button"
                  className="inline-flex items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2.5 rounded-full text-stone-700 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                {step < TOTAL_STEPS - 1 ? (
                  <button
                    type="button" onClick={next}
                    data-testid="wizard-next-button"
                    className="inline-flex items-center gap-1.5 sm:gap-2 bg-saffron-500 hover:bg-saffron-600 text-white rounded-full px-5 sm:px-7 py-2.5 sm:py-3 text-sm sm:text-base font-medium shadow-sm hover:shadow-md transition-all btn-shine"
                  >
                    Continue <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button" onClick={handleSubmit} disabled={submitting}
                    data-testid="wizard-submit-button"
                    className="inline-flex items-center gap-1.5 sm:gap-2 bg-ink-900 hover:bg-ink-800 text-white rounded-full px-5 sm:px-7 py-2.5 sm:py-3 text-sm sm:text-base font-medium shadow-sm hover:shadow-md transition-all disabled:opacity-70 btn-shine"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {submitting ? "Submitting..." : "Submit Feedback"}
                  </button>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function BasicFields({ fields, basic, setBasic }) {
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      {fields.map((f) => (
        <div key={f.key} className={f.key === "city_branch" || f.key === "university_name" ? "sm:col-span-2" : ""}>
          <label className="block text-sm font-medium text-stone-800 mb-2">
            {f.label} {f.required && <span className="text-saffron-600">*</span>}
          </label>
          <input
            type={f.type === "tel" ? "tel" : "text"}
            value={basic[f.key] ?? ""}
            readOnly={f.readonly}
            placeholder={f.placeholder || ""}
            onChange={(e) => setBasic((b) => ({ ...b, [f.key]: e.target.value }))}
            data-testid={`basic-input-${f.key}`}
            className={`w-full rounded-xl border bg-white px-4 py-3 text-stone-900 placeholder:text-stone-400 outline-none transition-all
              ${f.readonly ? "border-stone-100 bg-stone-50 cursor-not-allowed" : "border-stone-200 focus:border-saffron-500 focus:ring-2 focus:ring-saffron-500/15"}`}
          />
        </div>
      ))}
    </div>
  );
}

function QuestionField({ q, value, onChange }) {
  return (
    <div data-testid={`question-${q.id}`}>
      <div className="flex items-start gap-3">
        <div className="font-serif text-lg sm:text-xl text-saffron-600 leading-tight shrink-0 mt-0.5">{String(q.n).padStart(2, "0")}.</div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] sm:text-lg text-stone-900 font-medium leading-snug">{q.q}</div>
          <div className="mt-4">
            {q.type === "radio" && <RadioGroup q={q} value={value} onChange={onChange} />}
            {q.type === "checkbox" && <CheckboxGroup q={q} value={value || []} onChange={onChange} />}
            {q.type === "textarea" && (
              <textarea
                rows={4}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={q.placeholder || ""}
                data-testid={`textarea-${q.id}`}
                className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-stone-900 placeholder:text-stone-400 outline-none transition-all focus:border-saffron-500 focus:ring-2 focus:ring-saffron-500/15"
              />
            )}
            {q.type === "stars" && (
              <>
                <StarRating value={value || 0} onChange={onChange} max={q.maxStars || 5} testId={`stars-${q.id}`} />
                <AppreciationNote note={appreciateStars(value)} testId={`appreciate-stars-${q.id}`} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AppreciationNote({ note, testId }) {
  if (!note) return null;
  const toneCls =
    note.tone === "positive"
      ? "bg-lime-50 border-lime-200 text-lime-700"
      : note.tone === "negative"
      ? "bg-rose-50 border-rose-200 text-rose-700"
      : "bg-saffron-50 border-saffron-200 text-saffron-700";
  const Icon = note.tone === "positive" ? ThumbsUp : note.tone === "negative" ? Heart : Sparkles;
  return (
    <motion.div
      key={note.message}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      data-testid={testId}
      className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs sm:text-[13px] ${toneCls}`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={2} />
      <span className="leading-tight">{note.message}</span>
    </motion.div>
  );
}

function RadioGroup({ q, value, onChange }) {
  const note = appreciate(value);
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {q.options.map((opt) => {
          const active = value === opt;
          return (
            <button
              type="button" key={opt} onClick={() => onChange(opt)}
              data-testid={`radio-${q.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-left px-4 py-3 rounded-xl border transition-all text-sm active:scale-[0.985]
                ${active
                  ? "border-saffron-500 bg-saffron-50 text-stone-900 shadow-[inset_0_0_0_1px_rgba(30,144,215,0.4)]"
                  : "border-stone-200 bg-white hover:border-saffron-300 hover:bg-saffron-50/40 text-stone-700"}`}
            >
              <span className="inline-flex items-center gap-2.5">
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors
                  ${active ? "border-saffron-500" : "border-stone-300"}`}>
                  {active && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 22 }} className="w-2 h-2 rounded-full bg-saffron-500" />}
                </span>
                {opt}
              </span>
            </button>
          );
        })}
      </div>
      <AnimatePresence mode="wait">
        {note && <AppreciationNote note={note} testId={`appreciate-${q.id}`} />}
      </AnimatePresence>
    </>
  );
}

function CheckboxGroup({ q, value, onChange }) {
  const toggle = (opt) => {
    const set = new Set(value);
    if (set.has(opt)) set.delete(opt); else set.add(opt);
    onChange(Array.from(set));
  };
  const [otherVal, setOtherVal] = React.useState(() => {
    const found = (value || []).find((v) => v?.startsWith("Other: "));
    return found ? found.replace("Other: ", "") : "";
  });
  const setOther = (v) => {
    setOtherVal(v);
    const without = (value || []).filter((x) => !x.startsWith("Other:"));
    onChange(v.trim() ? [...without, `Other: ${v.trim()}`] : without);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
      {q.options.map((opt) => {
        const active = (value || []).includes(opt);
        return (
          <button
            type="button" key={opt} onClick={() => toggle(opt)}
            data-testid={`checkbox-${q.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
            className={`text-left px-4 py-3 rounded-xl border transition-all text-sm active:scale-[0.985]
              ${active
                ? "border-saffron-500 bg-saffron-50 text-stone-900"
                : "border-stone-200 bg-white hover:border-saffron-300 hover:bg-saffron-50/40 text-stone-700"}`}
          >
            <span className="inline-flex items-center gap-2.5">
              <span className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors
                ${active ? "bg-saffron-500 border-saffron-500" : "border-stone-300"}`}>
                {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </span>
              {opt}
            </span>
          </button>
        );
      })}
      {q.allowOther && (
        <input
          type="text" placeholder="Other (please specify)"
          value={otherVal} onChange={(e) => setOther(e.target.value)}
          data-testid={`checkbox-other-${q.id}`}
          className="sm:col-span-2 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-500/15"
        />
      )}
    </div>
  );
}
